import {InstagramOutlined, TikTokOutlined, YoutubeFilled} from "@ant-design/icons";
import {IG_COLOR, TT_COLOR, YT_COLOR} from "../../../common/constants";
import * as React from "react";
import {Space, Typography} from "antd";
import {generate} from "@ant-design/colors";

export const toEUR = (c: number) => `€${c.toLocaleString()}`;
export const YT_ICON = <YoutubeFilled style={{fontSize: 22, color: YT_COLOR}}/>;
export const IG_ICON = <InstagramOutlined style={{fontSize: 22, color: IG_COLOR}}/>;
export const TT_ICON = <TikTokOutlined style={{fontSize: 22, color: TT_COLOR}}/>;
const PricingTitleComponent = (icon: JSX.Element) => (title: string) =>
    (
        <Space size={5} align='baseline'>
            {icon}
            <Typography.Text>{title}</Typography.Text>
        </Space>
    );
export const TitleYT = PricingTitleComponent(YT_ICON);
export const TitleTT = PricingTitleComponent(TT_ICON);
export const TitleIG = PricingTitleComponent(IG_ICON);

export function formatFollowers(num) {
    if (num < 1000) {
        return num.toString();
    }

    const units = ["K", "M", "B", "T"];
    const order = Math.floor(Math.log10(num) / 3);
    const unitname = units[order - 1];

    const scaledNum = num / Math.pow(1000, order);
    const roundedNum = Math.round(scaledNum * 10) / 10;

    return roundedNum % 1 === 0 ? `${roundedNum.toFixed(0)}${unitname}` : `${roundedNum.toFixed(1)}${unitname}`;
}

export function formatSocialMediaAccountName(url) {
    if (!url && url !== 0) return '';
    // Be defensive: accept non-strings but coerce safely
    if (typeof url !== 'string') {
        try {
            return String(url);
        } catch (e) {
            return '';
        }
    }

    // Ensure URL has a scheme so `new URL()` won't throw for scheme-less values
    let candidate = url.trim();
    if (candidate === '') return '';
    if (!/^https?:\/\//i.test(candidate)) {
        candidate = `https://${candidate}`;
    }

    let urlObj: URL | null = null;
    try {
        urlObj = new URL(candidate);
    } catch (e) {
        // If it's not a valid URL, try to return a sanitized short-form (remove trailing slashes)
        const noSlash = url.replace(/\/+$/,'');
        const lastSegment = noSlash.split('/').filter(Boolean).pop();
        return lastSegment ? (lastSegment.startsWith('@') ? lastSegment : `@${lastSegment}`) : url;
    }

    const pathParts = urlObj.pathname.split('/').filter(part => part !== '');
    // Normalize hostname to handle www., m., and other subdomains
    const hostname = urlObj.hostname.replace(/^www\.|^m\./i, '').toLowerCase();

    // Helper to return safe handle
    const safeHandle = (candidateHandle?: string) => {
        if (!candidateHandle) return url;
        return candidateHandle.startsWith('@') ? candidateHandle : `@${candidateHandle}`;
    }

    // Handle common variants and be defensive to avoid throwing
    if (hostname.includes('youtube.com')) {
        if (pathParts[0] === 'channel') {
            return pathParts[1] || url;
        } else if (pathParts[0] === 'c') {
            return safeHandle(pathParts[1]);
        } else if (pathParts[0] && pathParts[0].includes('@')) {
            return pathParts[0];
        } else if (pathParts[0]) {
            return safeHandle(pathParts[0]);
        }
        return url;
    } else if (hostname === 'youtu.be') {
        return pathParts[0] || url;
    } else if (hostname.includes('instagram.com')) {
        if (pathParts[0] && pathParts[0].includes('@')) {
            return pathParts[0];
        } else if (pathParts[0]) {
            return safeHandle(pathParts[0]);
        }
        return url;
    } else if (hostname.includes('tiktok.com')) {
        if (pathParts[0] && pathParts[0].includes('@')) {
            return pathParts[0];
        } else if (pathParts[0]) {
            return safeHandle(pathParts[0]);
        }
        return url;
    }

    // Fallback: try to extract last meaningful segment, else return original input
    const lastSeg = pathParts.reverse().find(Boolean);
    return lastSeg ? (lastSeg.startsWith('@') ? lastSeg : `@${lastSeg}`) : url;
}


// Function to convert hex to RGB
function hexToRGB(hex: string) {
    // Remove the leading # if present
    if (!hex || typeof hex !== 'string') return null;
    hex = hex.replace(/^#/, '').trim();
    // support short hex like 'abc' -> 'aabbcc'
    if (/^[0-9a-fA-F]{3}$/.test(hex)) {
        hex = hex.split('').map(c => c + c).join('');
    }
    if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return {r, g, b}
}

function perceivedBrightness(r, g, b) {
    // Using the luminance formula from WCAG 2.0
    if (r == null || g == null || b == null || Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
        // default to fairly bright so we don't accidentally return extremely dark colors
        return 255;
    }
    return 0.299 * r + 0.587 * g + 0.114 * b;
}

// Function to determine brightness and return appropriate color
export function getPaletteColor(primary_color: string, palette: string[]) {
    if (!Array.isArray(palette) || palette.length === 0) return primary_color || '#000000';
    const rgb = hexToRGB(primary_color);
    if (!rgb) {
        // If primary color can't be parsed, return a safe palette color (last one if present)
        return palette[Math.min(palette.length - 1, 9)];
    }
    const {r, g, b} = rgb;
    let brightness = perceivedBrightness(r, g, b);

    if (brightness >= 128) {
        // prefer a lighter index (if available) otherwise last
        return palette[Math.min(9, palette.length - 1)];
    } else if (brightness > 70) {
        // medium brightness -> try a mid palette color
        const base = palette[0] || '#ffffff';
        const midPalette = generate(base);
        const fallbackIndex = Math.min(9, midPalette.length - 1);
        return midPalette[fallbackIndex] || palette[Math.min(9, palette.length - 1)];
    } else {
        // dark background -> choose a darker palette entry (first or 0)
        return palette[0] || (palette.length > 1 ? palette[1] : primary_color || '#000000');
    }
}

export function formatLastFetchAt(lastFetchAt?: string): string {
    if (!lastFetchAt) return 'Never updated';
    const diffDays = Math.floor((Date.now() - new Date(lastFetchAt).getTime()) / 86400000);
    if (diffDays === 0) return 'Updated today';
    if (diffDays === 1) return 'Updated yesterday';
    return `Updated ${diffDays} days ago`;
}